import { useState, useCallback, useMemo, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Heading } from "@/components/ui/Heading";
import { Text } from "@/components/ui/Text";
import {
  Loader2,
  Globe,
  ChevronUp,
  Info,
  ChevronsUpDown,
} from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { CreditIcon } from "@/components/icons/CreditIcon";
import { usePersonaThumbFallback } from "@/components/shared/PersonaThumbImg";
import { cn } from "@/lib/utils";
import { ROOM_LANGUAGES } from "@/lib/constants/languages";
import {
  RoomVisibility,
  StudyContext,
} from "@digital-twins/shared-types";
import type {
  StimulusImage,
  StimulusImagePolicy,
} from "@digital-twins/shared-types";

import { AudienceSelector } from "./AudienceSelector";
import { TwinSuggestions } from "./TwinSuggestions";
import { StartInSlack } from "./StartInSlack/StartInSlack";
import { useSlackTwinsEnabled } from "@/hooks/useSlackTwinsEnabled";
import { TwinCardFan } from "@/components/chat/TwinCardFan/TwinCardFan";
import { TrialLimitUpgradeModal } from "@/components/billing/TrialLimitUpgradeModal";
import {
  useComposeModal,
  InitialTwin,
  TwinSuggestion,
} from "./useComposeModal";
export type { TwinSuggestion };
import { AudienceSummary, populationsApi } from "@/lib/api";
import { resolveAudienceCanonicalCoverUrl } from "@/pages/AudiencesPage/utils/audience-presentation";
import {
  CHAT_CREDITS_PER_MSG,
  ROOM_CREDITS_PER_MSG,
} from "@/lib/billing/credit-rates";
import type { ColumnMetadataResult } from "@/types/components/audience-filter-modal";
import type { PopulationFilter } from "@/types/routes/test-results";

type StepKey = "audience" | "twins";

interface ComposeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialTwin?: InitialTwin | null;
  initialAudienceId?: string | null;
  initialMessage?: string | null;
  groupMode?: boolean;
  lockedAudienceId?: string | null;
  studyContext?: StudyContext | null;
  stimulusImages?: Array<StimulusImage> | null;
  stimulusImagePolicy?: StimulusImagePolicy | null;
  restrictedPersonaIds?: Array<string> | null;
  /** All study respondents pre-loaded — bypasses the population API fetch */
  studyRespondents?: Array<TwinSuggestion> | null;
  initialLanguage?: string | null;
  /**
   * Audience-only reuse (e.g. Quick Study): when true, picking an audience
   * immediately fires `onConfirm` with the chosen audience and the modal closes —
   * the twin-picker step and the room-creation CTA are skipped entirely. The
   * parent owns what happens next (no chat room is created). Default flow is
   * unchanged when omitted.
   */
  audienceOnly?: boolean;
  /** Called in `audienceOnly` mode once an audience is picked. */
  onConfirm?: (result: { audienceId: string }) => void;
  /**
   * Quick Study inline focus groups: twin picker only — no room is
   * created here; the parent launches the room once the marketer confirms.
   */
  focusGroupPickerOnly?: boolean;
  /** Called in `focusGroupPickerOnly` mode once twins are chosen. */
  onFocusGroupConfirm?: (result: {
    participantPersonaIds: Array<string>;
  }) => void;
  /** Pre-loaded audience metadata when opening from audience detail (before populations list resolves) */
  initialAudienceSnapshot?: AudienceSummary | null;
  /** Shared-link mode: read twins and create the room through the public API. */
  guestScope?: { shareToken: string } | null;
  /** Guest mode has no `/chats/:token` route — the parent renders the room. */
  onRoomCreated?: (roomToken: string) => void;
}

export function ComposeModal(props: ComposeModalProps) {
  const {
    open,
    onOpenChange,
    initialTwin,
    initialAudienceId,
    initialMessage,
    groupMode = false,
    lockedAudienceId,
    studyContext,
    stimulusImages,
    stimulusImagePolicy,
    restrictedPersonaIds,
    studyRespondents,
    initialLanguage,
    audienceOnly = false,
    onConfirm,
    focusGroupPickerOnly = false,
    onFocusGroupConfirm,
    initialAudienceSnapshot,
    guestScope,
    onRoomCreated,
  } = props;
  const { t } = useTranslation("app");

  const {
    audiences,
    audiencesLoading,
    selectedAudienceId,
    setSelectedAudienceId,
    selectedAudience,
    selectedTwins,
    selectedTwinIds,
    toggleTwin,
    twins,
    twinsLoading,
    twinsLoadingMore,
    twinsHasMore,
    loadMoreTwins,
    twinSearch,
    setTwinSearch,
    twinFilters,
    populationColumns,
    columnMetadataTotalRows,
    populationColumnGroups,
    columnNameToAttrKey,
    aiChips,
    handleAiApplyFilters,
    handleRemoveAiChip,
    handleOpenFilterModalPrefilled,
    twinSelectionTags,
    handleFiltersChange,
    language,
    setLanguage,
    visibility,
    setVisibility,
    isCreating,
    error,
    canCreate,
    hasUnreadyTwins,
    createRoom,
    isFocusGroupRoom,
    isAudienceLocked,
    trialRoomLimitOpen,
    closeTrialRoomLimit,
  } = useComposeModal({
    open,
    initialTwin,
    initialAudienceId,
    initialMessage,
    groupMode,
    lockedAudienceId,
    studyContext,
    stimulusImages,
    stimulusImagePolicy,
    restrictedPersonaIds,
    studyRespondents,
    initialLanguage,
    initialAudienceSnapshot,
    guestScope,
    onRoomCreated,
  });

  const [currentStep, setCurrentStep] = useState<StepKey>("audience");

  const studyRespondentsRef = useRef(studyRespondents);
  studyRespondentsRef.current = studyRespondents;

  const handleStudyPreviewFilter = useCallback(
    async (
      filter: PopulationFilter,
    ): Promise<{ matchingCount: number; totalCount: number }> => {
      const source = studyRespondentsRef.current;
      if (!source || !Array.isArray(source) || !selectedAudienceId) {
        return { matchingCount: 0, totalCount: 0 };
      }
      const personaTokens = source.map((t) => t.personaId);
      return populationsApi.previewFilter(
        selectedAudienceId,
        filter,
        personaTokens,
      );
    },
    [selectedAudienceId],
  );

  // Study mode: supply prefetched column data so AudienceFilterModal never
  // falls back to the /columns API (which would show the full population count).
  // Uses rawAttributes (normalized DB keys) from studyRespondents so that
  // queryKey from column metadata matches directly — no heuristic matching.
  const prefetchedFilterData = useMemo((): ColumnMetadataResult | null => {
    if (studyRespondents === undefined) return null;

    const valuesByQueryKey = new Map<string, Set<string>>();
    if (studyRespondents) {
      for (const twin of studyRespondents) {
        const attrs = twin.rawAttributes ?? twin.attributes ?? {};
        for (const [key, val] of Object.entries(attrs)) {
          if (val === null || val === undefined) continue;
          if (!valuesByQueryKey.has(key)) valuesByQueryKey.set(key, new Set());
          const bucket = valuesByQueryKey.get(key)!;
          if (typeof val === "object" && !Array.isArray(val)) {
            for (const subVal of Object.values(
              val as Record<string, unknown>,
            )) {
              const leaf = String(subVal ?? "").trim();
              if (leaf) bucket.add(leaf);
            }
          } else if (Array.isArray(val)) {
            for (const item of val) {
              const leaf = String(item ?? "").trim();
              if (leaf) bucket.add(leaf);
            }
          } else {
            const leaf = String(val).trim();
            if (leaf) bucket.add(leaf);
          }
        }
      }
    }

    return {
      columns: populationColumns.map((col) => {
        const queryKey = col.queryKey ?? col.name;
        const found = valuesByQueryKey.get(queryKey);
        const uniqueValues =
          found && found.size > 0 ? Array.from(found).sort() : col.uniqueValues;
        return {
          name: queryKey,
          uniqueValues,
          displayLabel: col.displayLabel || col.name,
          canonicalId: col.canonicalId ?? null,
          category: col.canonicalId?.startsWith("std_dem")
            ? "demographics"
            : col.canonicalId?.startsWith("std_geo")
              ? "geography"
              : "other",
          isCustom: !col.canonicalId,
        };
      }),
      totalRows: columnMetadataTotalRows,
      groups:
        populationColumnGroups.length > 0 ? populationColumnGroups : undefined,
    };
  }, [
    studyRespondents,
    populationColumns,
    columnMetadataTotalRows,
    populationColumnGroups,
  ]);

  const handleFocusGroupConfirm = useCallback(() => {
    if (selectedTwinIds.length < 2) return;
    onFocusGroupConfirm?.({
      participantPersonaIds: selectedTwinIds,
    });
    onOpenChange(false);
  }, [selectedTwinIds, onFocusGroupConfirm, onOpenChange]);

  const handleStart = useCallback(() => {
    if (focusGroupPickerOnly) {
      handleFocusGroupConfirm();
      return;
    }
    void createRoom();
  }, [focusGroupPickerOnly, handleFocusGroupConfirm, createRoom]);

  // Auto-advance past audience step when audience or twin is pre-filled
  const hasAutoAdvanced = useRef(false);
  useEffect(() => {
    if (open && focusGroupPickerOnly && lockedAudienceId) {
      setCurrentStep("twins");
      return;
    }
    if (
      open &&
      (initialTwin || initialAudienceId || lockedAudienceId) &&
      selectedAudienceId &&
      !hasAutoAdvanced.current
    ) {
      hasAutoAdvanced.current = true;
      setCurrentStep("twins");
    }
    if (!open) {
      hasAutoAdvanced.current = false;
    }
  }, [
    open,
    focusGroupPickerOnly,
    initialTwin,
    initialAudienceId,
    lockedAudienceId,
    selectedAudienceId,
  ]);

  const showPreview = selectedAudienceId !== null;

  const goBackToAudience = useCallback(() => {
    setSelectedAudienceId(null);
    setCurrentStep("audience");
  }, [setSelectedAudienceId]);

  const handleAudienceSelect = useCallback(
    (id: string | null) => {
      // Audience-only reuse (Quick Study): confirm on pick, skip the twin step + room
      // creation, and close explicitly rather than relying on the parent unmounting us.
      if (audienceOnly) {
        if (id) {
          onConfirm?.({ audienceId: id });
          onOpenChange(false);
        }
        return;
      }
      setSelectedAudienceId(id);
      if (id) {
        setTimeout(() => setCurrentStep("twins"), 350);
      }
    },
    [audienceOnly, onConfirm, onOpenChange, setSelectedAudienceId],
  );

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) setCurrentStep("audience");
      onOpenChange(next);
    },
    [onOpenChange],
  );

  return (
    <>
      <TrialLimitUpgradeModal
        open={trialRoomLimitOpen}
        onClose={closeTrialRoomLimit}
        kind="rooms"
      />
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          className={cn(
            // Below md: one column (picker, then participants + Start pinned at
            // the bottom) with fluid widths; md and up keep the two-panel row.
            "flex min-h-[480px] max-h-[85dvh] w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl p-0 sm:p-0 sm:max-h-[80vh] md:flex-row",
            "transition-[max-width] duration-500 ease-[cubic-bezier(0.32,_0.72,_0,_1)]",
            showPreview ? "max-w-[1100px]" : "max-w-2xl",
          )}
          data-testid="compose-modal"
        >
          {/* ─── Left panel ─── */}
          <div
            className={cn(
              // Phone column: the picker takes the free height and scrolls; md and up
              // keep the original fixed-width, non-shrinking left panel.
              "flex min-h-0 flex-1 flex-col overflow-hidden md:flex-none",
              "transition-[width] duration-500 ease-[cubic-bezier(0.32,_0.72,_0,_1)]",
              showPreview ? "w-full md:w-[440px]" : "w-full",
            )}
          >
            <DialogTitle className="sr-only">{t("compose.start")}</DialogTitle>
            <DialogDescription className="sr-only">
              {t("conversations.pickTwins")}
            </DialogDescription>

            {/* ── Audience picker ── */}
            {currentStep === "audience" && !focusGroupPickerOnly && (
              <div className="flex flex-1 flex-col items-center justify-center px-8 py-10 animate-in fade-in duration-300">
                <HeroCards />

                <Heading level={1} size="2xl" className="mt-6 mb-2 text-center">
                  {t("compose.whoTalk")}
                </Heading>
                <Text
                  as="p"
                  size="sm"
                  color="muted"
                  className="mb-8 max-w-[280px] text-center leading-relaxed"
                >
                  {t("compose.pickAudienceBody")}
                </Text>

                <div className="w-full max-w-sm">
                  <AudienceSelector
                    audiences={audiences}
                    selectedId={selectedAudienceId}
                    onSelect={handleAudienceSelect}
                    isLoading={audiencesLoading}
                  />
                </div>
              </div>
            )}

            {/* ── Twin picker ── */}
            {currentStep === "twins" && (
              <div className="flex flex-1 flex-col overflow-hidden px-4 md:pl-8 md:pr-5">
                {/* Below md the header drops under the dialog's close X so the two never overlap. */}
                <div className="relative flex items-center justify-center pt-11 md:pt-4 animate-in fade-in slide-in-from-bottom-1 duration-300">
                  {!isAudienceLocked && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={goBackToAudience}
                      className="absolute left-0 h-8 rounded-lg px-3 text-xs"
                      data-testid="compose-prev-button"
                    >
                      <ChevronUp className="mr-1 h-3 w-3 -rotate-90" />
                      {t("back", { ns: "common" })}
                    </Button>
                  )}
                  <Heading
                    level={2}
                    size="2xl"
                    className="text-center text-xl md:text-2xl animate-in fade-in slide-in-from-bottom-1 duration-300"
                  >
                    {t("conversations.pickTwins")}
                  </Heading>
                </div>

                {error && (
                  <div className="mt-2 mr-0 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-2.5 text-sm text-destructive">
                    {error}
                  </div>
                )}

                <div className="flex w-full flex-1 flex-col overflow-hidden mt-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
                  <TwinSuggestions
                    twins={twins}
                    selectedIds={selectedTwinIds}
                    onToggle={toggleTwin}
                    isLoading={twinsLoading}
                    isLoadingMore={twinsLoadingMore}
                    hasMore={twinsHasMore}
                    onLoadMore={loadMoreTwins}
                    search={twinSearch}
                    onSearchChange={setTwinSearch}
                    filters={twinFilters}
                    onFiltersChange={handleFiltersChange}
                    populationId={selectedAudienceId!}
                    populationName={selectedAudience?.name ?? ""}
                    coverImageUrl={resolveAudienceCanonicalCoverUrl(
                      selectedAudience,
                    )}
                    audienceType={selectedAudience?.audienceType ?? null}
                    aiChips={aiChips}
                    onAiApplyFilters={handleAiApplyFilters}
                    onRemoveAiChip={handleRemoveAiChip}
                    onOpenFilterModalPrefilled={handleOpenFilterModalPrefilled}
                    totalPersonas={columnMetadataTotalRows}
                    studyTwins={
                      studyRespondents !== undefined
                        ? studyRespondents
                        : undefined
                    }
                    columnNameToAttrKey={columnNameToAttrKey}
                    prefetchedFilterData={prefetchedFilterData}
                    onPreviewFilter={
                      // The preview endpoint is authenticated-only; guests filter
                      // client-side against the study respondents they already have.
                      studyRespondents !== undefined && !guestScope
                        ? handleStudyPreviewFilter
                        : undefined
                    }
                    minPersonas={groupMode || focusGroupPickerOnly ? 2 : 1}
                    audienceTags={selectedAudience?.audienceTags}
                  />
                </div>
              </div>
            )}
          </div>

          {/* ─── Right: Preview panel ─── */}
          <div
            className={cn(
              "flex flex-col overflow-hidden",
              "transition-[width,opacity] duration-500 ease-[cubic-bezier(0.32,_0.72,_0,_1)]",
              showPreview
                ? "w-full shrink-0 border-t border-border opacity-100 md:w-[660px] md:shrink md:border-l md:border-t-0"
                : "w-0 opacity-0",
            )}
          >
            {showPreview && (
              <PreviewPane
                audience={selectedAudience}
                isFocusGroupRoom={isFocusGroupRoom}
                selectedTwins={selectedTwins}
                isCreating={isCreating}
                canCreate={canCreate}
                hasUnreadyTwins={hasUnreadyTwins}
                onStart={handleStart}
                startLabel={
                  focusGroupPickerOnly
                    ? t("conversations.startFocusGroup")
                    : undefined
                }
                hideExtraSettings={focusGroupPickerOnly}
                language={language}
                onLanguageChange={setLanguage}
                visibility={visibility}
                onVisibilityChange={setVisibility}
                twinSelectionTags={twinSelectionTags}
                slackPopulationToken={
                  guestScope || focusGroupPickerOnly || audienceOnly
                    ? null
                    : selectedAudienceId
                }
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

/* ─── Hero Cards (step 1 visual) ─── */

const AVATAR_POOL = [
  "https://storage.googleapis.com/fairgen-twins-profiles/persona-images/joyful-community-library-goer.png",
  "https://storage.googleapis.com/fairgen-twins-profiles/persona-images/thoughtful-home-chef.png",
  "https://storage.googleapis.com/fairgen-twins-profiles/persona-images/south-asian-female-peaceful-contemplation.png",
  "https://storage.googleapis.com/fairgen-twins-profiles/persona-images/thoughtful-innovator-male.png",
  "https://storage.googleapis.com/fairgen-twins-profiles/persona-images/warm-smile-carpenter-at-cafe.png",
  "https://storage.googleapis.com/fairgen-twins-profiles/persona-images/industrious-young-professional.png",
  "https://storage.googleapis.com/fairgen-twins-profiles/persona-images/fashion-forward-traveler.png",
  "https://storage.googleapis.com/fairgen-twins-profiles/persona-images/relaxed-urban-hiker.png",
  "https://storage.googleapis.com/fairgen-twins-profiles/persona-images/senior-gardening-enthusiast.png",
  "https://storage.googleapis.com/fairgen-twins-profiles/persona-images/determined-city-explorer.png",
  "https://storage.googleapis.com/fairgen-twins-profiles/persona-images/graceful-dancer-grandma.png",
  "https://storage.googleapis.com/fairgen-twins-profiles/persona-images/middle-eastern-modern-simplicity.png",
];

function HeroCards() {
  const picks = useMemo(() => {
    const shuffled = [...AVATAR_POOL].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 3);
  }, []);

  return <TwinCardFan cards={picks.map((url) => ({ id: url, imageUrl: url }))} />;
}

/* ─── Language Chip (auto-width to selected value) ─── */

interface LanguageChipProps {
  language: string;
  onLanguageChange: (code: string) => void;
}

function LanguageChip(props: LanguageChipProps) {
  const { language, onLanguageChange } = props;
  const { t } = useTranslation("common");
  const { t: tApp } = useTranslation("app");
  const selectedName = t(`languageNames.${language}`, {
    defaultValue: language,
  });

  return (
    <label className="relative inline-flex cursor-pointer items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-xs font-medium text-foreground hover:border-primary/30 transition-colors">
      <Globe className="h-3 w-3 shrink-0" />
      <span className="pointer-events-none whitespace-nowrap">
        {selectedName}
      </span>
      <ChevronsUpDown className="h-2.5 w-2.5 shrink-0 text-muted-foreground" />
      <select
        value={language}
        onChange={(e) => onLanguageChange(e.target.value)}
        aria-label={tApp("conversations.languageAria")}
        data-testid="compose-language-select"
        className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
      >
        {ROOM_LANGUAGES.map((lang) => (
          <option key={lang.code} value={lang.code}>
            {t(`languageNames.${lang.code}`)}
          </option>
        ))}
      </select>
    </label>
  );
}

/* ─── Option Chip ─── */

interface OptionChipProps extends React.HTMLAttributes<HTMLElement> {
  icon: React.ReactNode;
  active?: boolean;
  children: React.ReactNode;
}

function OptionChip(props: OptionChipProps) {
  const { icon, active, children, onClick, className, ...rest } = props;

  const Tag = onClick ? "button" : "div";

  return (
    <Tag
      type={onClick ? "button" : undefined}
      onClick={onClick as any}
      className={cn(
        "inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors",
        active
          ? "border-primary/40 bg-primary/5 text-primary"
          : "border-border bg-background text-foreground",
        onClick && "cursor-pointer hover:border-primary/30",
        className,
      )}
      {...rest}
    >
      {icon}
      {children}
    </Tag>
  );
}

/* ─── Preview Pane ─── */

interface PreviewPaneProps {
  audience: AudienceSummary | null;
  isFocusGroupRoom: boolean;
  selectedTwins: Array<TwinSuggestion>;
  isCreating: boolean;
  canCreate: boolean;
  hasUnreadyTwins: boolean;
  onStart: () => void;
  startLabel?: string;
  hideExtraSettings?: boolean;
  language: string;
  onLanguageChange: (code: string) => void;
  visibility: RoomVisibility;
  onVisibilityChange: (vis: RoomVisibility) => void;
  twinSelectionTags: Record<
    string,
    Array<{ chip: string; displayQuestion: string; displayAnswer: string }>
  >;
  /** Slack Twins E6: offers "Start in Slack" when set; null hides it. */
  slackPopulationToken?: string | null;
}

/** One selected-twin avatar in the overlapping "your participants" stack — its
 *  own component so the thumbnail/original fallback state (finding 7, image
 *  compression) is scoped per twin instead of being called inside `.map`. */
function ComposeModalTwinStackAvatar({
  imageUrl,
  displayName,
  zIndex,
}: {
  imageUrl: string;
  displayName: string;
  zIndex: number;
}) {
  const { displayUrl, handleImageError } = usePersonaThumbFallback(imageUrl, 64);

  return (
    <img
      src={displayUrl}
      alt={displayName}
      className="h-10 w-10 rounded-full border-2 border-background object-cover animate-in fade-in zoom-in-90 duration-200"
      style={{ zIndex }}
      onError={(e) => {
        const target = e.target as HTMLImageElement;
        handleImageError(() => {
          target.src = "/images/twin-fallback.png";
        });
      }}
    />
  );
}

function PreviewPane(props: PreviewPaneProps) {
  const { t, i18n } = useTranslation("app");
  const {
    audience,
    isFocusGroupRoom,
    selectedTwins,
    isCreating,
    canCreate,
    hasUnreadyTwins,
    onStart,
    startLabel,
    hideExtraSettings = false,
    language,
    onLanguageChange,
    visibility,
    onVisibilityChange,
    twinSelectionTags,
    slackPopulationToken = null,
  } = props;

  const isSlackTwinsEnabled = useSlackTwinsEnabled();

  const coverUrl = resolveAudienceCanonicalCoverUrl(audience);
  const hasTwins = selectedTwins.length > 0;
  const rawPersonaCount = audience?.personaCount;
  const personaCount = typeof rawPersonaCount === "number" ? rawPersonaCount : 0;

  // Settings chips: visible once Start is active, kept while Start loads.
  // StartInSlack puts them on the Where line.
  const settingsChips =
    canCreate || isCreating ? (
      <>
        {!hideExtraSettings && (
          <LanguageChip
            language={language}
            onLanguageChange={onLanguageChange}
          />
        )}
        {(() => {
          const tier = audience?.tier ?? "tier1";
          const rateMap = isFocusGroupRoom
            ? ROOM_CREDITS_PER_MSG
            : CHAT_CREDITS_PER_MSG;
          const quoted = isFocusGroupRoom
            ? audience?.focusGroupCreditsPerMessage
            : audience?.chatCreditsPerMessage;
          const rate = quoted ?? rateMap[tier] ?? 1;
          return (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <OptionChip
                      icon={<CreditIcon className="h-3 w-3" />}
                      data-testid="chat-credit-rate-chip"
                      className="border-0"
                    >
                      {t("conversations.perMessage", { count: rate })}
                      <Info className="h-2.5 w-2.5 ml-0.5 text-muted-foreground" />
                    </OptionChip>
                  </span>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  className="text-xs max-w-[160px] text-center"
                >
                  {t("conversations.creditsPerMessage")}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        })()}
      </>
    ) : null;

  return (
    <div className="flex h-full w-full flex-col animate-in fade-in duration-300">
      {/* Cover hero */}
      {/* Hero keeps one height whether or not twins are picked: the panel
          below reserves the participants block up front. Hidden below md,
          where the twin picker already names the audience. */}
      <div
        className="relative hidden flex-1 overflow-hidden md:block"
        data-testid="compose-preview-hero"
      >
        {coverUrl ? (
          <img
            src={coverUrl}
            alt={audience?.name ?? ""}
            className="absolute inset-0 h-full w-full object-cover animate-in zoom-in-105 duration-700"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-primary/25 via-primary/10 to-muted/30" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />

        <div className="absolute bottom-0 left-0 right-0 px-6 pb-5">
          {personaCount > 0 && (
            <Text
              as="p"
              size="xs"
              weight="medium"
              className="mb-2 text-foreground/70 drop-shadow-sm"
            >
              {t("conversations.twinsCount", {
                formatted: personaCount.toLocaleString(i18n.language),
              })}
            </Text>
          )}
          <Heading
            level={2}
            size="2xl"
            className="text-foreground drop-shadow-sm"
          >
            {audience?.name ?? t("conversations.selectAnAudience")}
          </Heading>
          {(audience?.stageMetadata?.tagline || audience?.aiDescription) && (
            <Text
              as="p"
              size="sm"
              color="muted"
              className="mt-1.5 line-clamp-2 drop-shadow-sm"
            >
              {audience.stageMetadata?.tagline || audience.aiDescription}
            </Text>
          )}
        </div>
      </div>

      {/* Participants + CTA + settings */}
      <div
        className="flex shrink-0 flex-col justify-end space-y-4 border-border px-6 py-5 animate-in fade-in slide-in-from-bottom-3 duration-300 md:border-t"
        data-testid="compose-preview-footer"
      >
        {hasTwins && (
          <>
            <Text
              as="p"
              size="xs"
              color="muted"
              weight="medium"
            >
              {t("conversations.yourParticipants")}
            </Text>
            <div className="flex items-center gap-4">
              <div className="flex -space-x-2.5">
                {selectedTwins.slice(0, 6).map((twin, i) => {
                  const displayName =
                    twin.resolvedIdentity?.display_name ?? twin.personaName;
                  return twin.imageUrl ? (
                    <ComposeModalTwinStackAvatar
                      key={twin.personaId}
                      imageUrl={twin.imageUrl}
                      displayName={displayName}
                      zIndex={selectedTwins.length - i}
                    />
                  ) : (
                    <div
                      key={twin.personaId}
                      className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-background bg-muted text-xs font-medium text-muted-foreground"
                      style={{ zIndex: selectedTwins.length - i }}
                    >
                      {displayName.charAt(0).toUpperCase()}
                    </div>
                  );
                })}
                {selectedTwins.length > 6 && (
                  <div className="flex h-10 w-10 items-center justify-center rounded-full border-2 border-background bg-muted text-xs font-medium text-muted-foreground">
                    +{selectedTwins.length - 6}
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <Text as="p" size="sm" weight="medium" className="truncate">
                  {selectedTwins
                    .slice(0, 3)
                    .map(
                      (t) => t.resolvedIdentity?.display_name ?? t.personaName,
                    )
                    .join(", ")}
                  {selectedTwins.length > 3 &&
                    ` ${t("conversations.moreTwins", { count: selectedTwins.length - 3 })}`}
                </Text>
                {selectedTwins.some(
                  (t) => (twinSelectionTags[t.personaId]?.length ?? 0) > 0,
                ) &&
                  (() => {
                    const chipCounts = new Map<string, number>();
                    for (const t of selectedTwins) {
                      for (const tag of twinSelectionTags[t.personaId] ?? []) {
                        chipCounts.set(
                          tag.chip,
                          (chipCounts.get(tag.chip) ?? 0) + 1,
                        );
                      }
                    }
                    return (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {[...chipCounts.entries()].map(([chip, count]) => (
                          <span
                            key={chip}
                            className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground"
                          >
                            {chip}
                            {count < selectedTwins.length ? ` (${count})` : ""}
                          </span>
                        ))}
                      </div>
                    );
                  })()}
              </div>
            </div>
          </>
        )}

        {hasUnreadyTwins && (
          <Text as="p" size="xs" color="muted" className="text-center">
            {t("conversations.unreadyTwins")}
          </Text>
        )}

        <StartInSlack
          settings={settingsChips}
          hasParticipants={selectedTwins.length > 0}
          isEnabled={slackPopulationToken !== null && isSlackTwinsEnabled}
          populationToken={slackPopulationToken}
          participantPersonaIds={selectedTwins.map((twin) => twin.personaId)}
        >
          <Button
            variant="cta"
            onClick={onStart}
            disabled={!canCreate}
            className="h-11 w-full rounded-xl text-sm"
            data-testid="compose-start-button"
          >
            {isCreating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {startLabel ??
              (isFocusGroupRoom
                ? t("conversations.startFocusGroup")
                : t("conversations.startInterview"))}
          </Button>
        </StartInSlack>
      </div>
    </div>
  );
}
